import { ReactElement, ReactNode } from "react"
import { useParams } from "react-router-dom"

import AmendDeviceProvider from "./baseProviders/amendDeviceProvider"
import useGetTTRDeviceFormDataFromApiResponse from "../../../../hooks/useGetTTRDeviceFormDataFromApiResponse"
import useContinueDeviceDraft from "../../../../services/middleware/hooks/devices/ttr/useContinueDeviceDraft"
import { TTypeTestVersionPostBody } from "../../../../services/middleware/types/devices/ttr/publishDeviceTypes"

interface IContinueAmendmentUrlParams {
    deviceRef: string
}
interface IContinueAmendmentProviderProps {
    children: ReactNode
}

const ContinueAmendmentProvider = ({
    children,
}: IContinueAmendmentProviderProps): ReactElement => {
    const { deviceRef: urlDeviceRef } = useParams<
        keyof IContinueAmendmentUrlParams
    >() as IContinueAmendmentUrlParams

    const {
        data,
        isLoading,
        isValidating,
        error: continueAmendmentError,
    } = useContinueDeviceDraft(urlDeviceRef)

    const { formData, setFormData } = useGetTTRDeviceFormDataFromApiResponse(
        isLoading,
        isValidating,
        continueAmendmentError,
        data
    )

    const deviceId = data?.DeviceID ?? undefined
    const deviceRef = data?.DeviceRef ?? undefined

    const attachmentUUID = data?.Attachment_UUID ?? undefined
    const supersededDeviceId = data?.SupersededDeviceID ?? undefined
    const complianceComments = data?.ComplianceComments ?? undefined
    const complianceStatusNotes = data?.ComplianceStatusNotes ?? undefined

    const typeTestVersionPostBody: TTypeTestVersionPostBody | undefined = data
        ? {
              G98GBVersion: data.G98GBVersion ?? undefined,
              G98NIVersion: data.G98NIVersion ?? undefined,
              G99GBVersion: data.G99GBVersion ?? undefined,
              G99NIVersion: data.G99NIVersion ?? undefined,
          }
        : undefined

    return (
        <AmendDeviceProvider
            formData={formData}
            setFormData={setFormData}
            formDataIsLoading={isLoading}
            formDataError={continueAmendmentError}
            deviceId={deviceId}
            deviceRef={deviceRef}
            attachmentUUID={attachmentUUID}
            supersededDeviceID={supersededDeviceId}
            complianceComments={complianceComments}
            complianceStatusNotes={complianceStatusNotes}
            typeTestVersionPostBody={typeTestVersionPostBody}
        >
            {children}
        </AmendDeviceProvider>
    )
}

export default ContinueAmendmentProvider
